package components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import business.CampaignConfig
import business.defaultCampaignConfig
import calculator.ComparisonResult
import calculator.ScenarioId
import calculator.publicRanking
import format.formatInteger
import format.formatRub
import kotlin.math.roundToLong
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import services.LeadService
import services.LocalOnlyLeadService
import services.MetricsService
import services.FunnelEvent
import services.FunnelEventService
import services.NoopFunnelEventService
import services.SubmissionResult
import services.browserMetricsService
import services.toEventDetail
import services.toLeadSubmission

typealias LeadForm = services.LeadForm

internal const val APEC_EXPRESS_APPLICATION_URL = "https://apec-express.ru"
internal const val APEC_EXPRESS_APPLICATION_LABEL = "Оформить карту АТЭС"

internal sealed class LeadSubmissionState {
    data object Idle : LeadSubmissionState()
    data object Submitting : LeadSubmissionState()
    data object Submitted : LeadSubmissionState()
    data class Failed(val reason: String) : LeadSubmissionState()
}

internal data class FunnelState(
    val codeVisible: Boolean = false,
    val referralDiscountEnabled: Boolean = false,
    val leadSubmitted: Boolean = false,
    val metricsExport: String = "",
    val leadSubmissionState: LeadSubmissionState = LeadSubmissionState.Idle,
)

internal class LeadSubmissionGuard {
    private var state = State.Ready

    fun tryStart(): Boolean {
        if (state != State.Ready) {
            return false
        }
        state = State.InFlight
        return true
    }

    fun releaseAfterFailure() {
        if (state == State.InFlight) {
            state = State.Ready
        }
    }

    fun markSubmitted() {
        state = State.Submitted
    }

    private enum class State {
        Ready,
        InFlight,
        Submitted,
    }
}

@Composable
internal fun ReferralFunnel(
    comparison: ComparisonResult,
    leadForm: LeadForm,
    funnelState: FunnelState,
    onLeadFormChange: (LeadForm) -> Unit,
    onFunnelStateChange: (FunnelState) -> Unit,
    metricsService: MetricsService = browserMetricsService(),
    campaignConfig: CampaignConfig = defaultCampaignConfig,
    leadService: LeadService = LocalOnlyLeadService(),
    eventService: FunnelEventService = NoopFunnelEventService(),
    sessionId: String = "",
) {
    val apec = comparison.scenarios.single { it.id == ScenarioId.APEC }
    val eVisa = comparison.scenarios.single { it.id == ScenarioId.E_VISA }
    val noEVisa = comparison.scenarios.single { it.id == ScenarioId.NO_E_VISA }
    val best = publicRanking(comparison).first()
    val apecDeltaToBest = apec.totalRub - best.totalRub
    val referralDiscountRub = campaignConfig.referralDiscountRub
    val commissionRub = campaignConfig.commissionRub
    val apecTotalRub = apec.totalRub.roundToLong().toString()
    val eVisaTotalRub = eVisa.totalRub.roundToLong().toString()
    var metrics by remember(metricsService) { mutableStateOf(metricsService.summary()) }
    var calculationSaved by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val latestFunnelState by rememberUpdatedState(funnelState)
    val leadSubmissionGuard = remember { LeadSubmissionGuard() }

    fun trackMetric(event: String, detail: String) {
        metricsService.track(event, detail, best.title, apecTotalRub, eVisaTotalRub)
        metrics = metricsService.summary()
    }

    fun recordEvent(eventType: String, payloadJson: String) {
        scope.launch {
            try {
                eventService.record(FunnelEvent(eventType, sessionId, campaignConfig.campaignId, payloadJson = payloadJson))
            } catch (_: Throwable) {
                // Local calculator behavior must remain available if event delivery fails.
            }
        }
    }

    Div({ classes("referral-panel") }) {
        Div({ classes("referral-header") }) {
            Div {
                H2 { Text("Оформление карты АТЭС со скидкой") }
                P {
                    Text("Компания берет 80 000 RUB, по реферальному коду скидка ${formatRub(referralDiscountRub)}.")
                }
            }
            Div({ classes("referral-badge") }) {
                Span({ classes("summary-label") }) { Text("Комиссия") }
                Span({ classes("summary-value") }) { Text(formatRub(commissionRub)) }
            }
        }

        Div({ classes("funnel-grid") }) {
            Div({ classes("funnel-card") }) {
                Span({ classes("summary-label") }) { Text("Вывод по расчету") }
                P {
                    Text(funnelRecommendation(best.title, apecDeltaToBest, referralDiscountRub))
                }
                Ul({ classes("formula-list") }) {
                    Li { Text("АТЭС: ${formatRub(apec.totalRub)} за период") }
                    Li { Text("Штамп: ${formatRub(noEVisa.totalRub)} за период") }
                }
                Div({ classes("button-row") }) {
                    A(
                        href = APEC_EXPRESS_APPLICATION_URL,
                        attrs = {
                            classes("primary-button", "application-link")
                            attr("target", "_blank")
                            attr("rel", "noopener noreferrer")
                        },
                    ) {
                        Text(APEC_EXPRESS_APPLICATION_LABEL)
                    }
                    Button({
                        classes("secondary-button")
                        onClick {
                            calculationSaved = true
                            trackMetric("calculation_save", "best=${best.title};apec=$apecTotalRub")
                            recordEvent("calculation_save", """{"bestScenario":"${jsonEscape(best.title)}","apecTotalRub":$apecTotalRub}""")
                        }
                    }) {
                        Text("Зафиксировать расчет")
                    }
                }
                if (calculationSaved) {
                    P({ classes("save-status") }) {
                        Text("Расчет зафиксирован в этом сеансе.")
                    }
                }
            }

        }
    }
}

private fun jsonEscape(value: String): String = value.replace("\\", "\\\\").replace("\"", "\\\"")

private fun funnelRecommendation(bestTitle: String, apecDeltaToBest: Double, referralDiscountRub: Double): String {
    return if (bestTitle == "АТЭС") {
        "АТЭС уже лучший вариант в расчете. Реферальная скидка дополнительно улучшает экономику на ${formatRub(referralDiscountRub)}."
    } else if (apecDeltaToBest <= referralDiscountRub) {
        "АТЭС почти сравнялась с лучшим вариантом. Скидка ${formatRub(referralDiscountRub)} может сделать решение рациональным, если важны удобство и поездки по АТЭС."
    } else {
        "По текущим цифрам АТЭС дороже лучшего варианта на ${formatRub(apecDeltaToBest)}. Код все равно полезен тем, кто ценит удобство, fast-track и поездки по странам АТЭС."
    }
}
